import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { AddRounded, DeleteRounded } from '@mui/icons-material';
import { MANDATORY_MAPPING_KEY, addMandatoryMapping, removeMandatoryMapping } from 'utils/mandatoryFieldsLogic';
import { attrValAssocObj } from './data';
import RenderAttributeInputs from 'components/PimDashboard/CommonComponents/RenderAttributeInputs/RenderAttributeInputs';

const MandatoryMappingManager = ({ 
  attribute, 
  onSave,
  allAttributes = [],
  open,
  onClose 
}) => {
  const [mappings, setMappings] = useState(attribute?.data?.[MANDATORY_MAPPING_KEY] || []);
  const [formData, setFormData] = useState({ brand: [], category: [] });

  const brandAttr = allAttributes.find(a => a.key === 'brand');
  const categoryAttr = allAttributes.find(a => a.key === 'category');

  const brandAttrMultiple = brandAttr ? { ...brandAttr, input_type: 'MULTIPLE_SELECT' } : null;
  const categoryAttrMultiple = categoryAttr ? { ...categoryAttr, input_type: 'MULTIPLE_SELECT' } : null;

  const handleAddMapping = () => {
    if (!formData.brand?.length || !formData.category?.length) return;
    
    const categoryAttr = allAttributes.find(a => a.key === 'category');
    const newMappings = [];
    
    formData.brand.forEach(brand => {
      formData.category.forEach(category => {
        const categoryValue = categoryAttr?.[attrValAssocObj]?.find(v => v.value === category);
        const categoryBrands = categoryValue?.dependency?.brand || [];
        
        if (categoryBrands.includes(brand)) {
          newMappings.push({ brand, category, mandatory: true });
        }
      });
    });
    
    if (newMappings.length === 0) {
      alert('No valid brand-category combinations found based on dependencies');
      return;
    }
    
    const updatedMappings = [...mappings];
    newMappings.forEach(newMap => {
      const exists = updatedMappings.find(
        m => m.brand === newMap.brand && m.category === newMap.category
      );
      if (!exists) {
        updatedMappings.push(newMap);
      }
    });
    
    setMappings(updatedMappings);
    setFormData({ brand: [], category: [] });
  };

  const handleRemoveMapping = (brand, category) => {
    const updatedData = removeMandatoryMapping(
      { data: { [MANDATORY_MAPPING_KEY]: mappings } },
      brand,
      category
    );
    
    setMappings(updatedData[MANDATORY_MAPPING_KEY]);
  };

  const handleSave = () => {
    const updatedAttribute = {
      ...attribute,
      data: {
        ...attribute.data,
        [MANDATORY_MAPPING_KEY]: mappings
      }
    };
    onSave(updatedAttribute);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Manage Mandatory Mappings - {attribute?.name}
      </DialogTitle>
      
      <DialogContent>
        <Box mb={3}>
          <Typography variant="body2" color="textSecondary" mb={2}>
            Define which brand/category combinations require this field to be mandatory
          </Typography>
          
          <Box mb={2}>
            {brandAttrMultiple && categoryAttrMultiple ? (
              <>
                <RenderAttributeInputs
                  columns={[brandAttrMultiple, categoryAttrMultiple]}
                  data={formData}
                  setData={setFormData}
                  selectOptObjOptName={attrValAssocObj}
                  inputWidth="45%"
                />
                <Box mt={1}>
                  <Button
                    variant="contained"
                    startIcon={<AddRounded />}
                    onClick={handleAddMapping}
                    disabled={!formData.brand?.length || !formData.category?.length}
                  >
                    Add
                  </Button>
                </Box>
              </>
            ) : (
              <Typography variant="body2" color="error">
                Brand or Category attribute not found in system
              </Typography>
            )}
          </Box>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Brand</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mappings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No mappings defined. This field will use global NOTNULL constraint only.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                mappings.map((mapping, index) => (
                  <TableRow key={index}>
                    <TableCell>{mapping.brand || 'All'}</TableCell>
                    <TableCell>{mapping.category || 'All'}</TableCell>
                    <TableCell>
                      <Chip 
                        label="Mandatory" 
                        color="error" 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveMapping(mapping.brand, mapping.category)}
                      >
                        <DeleteRounded />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>
          Save Mappings
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MandatoryMappingManager;
